import React, { useState, useEffect, useRef, useCallback } from "react"
import MenuGroup from "aldoo-ra/MenuGroup"
import Icon from "aldoo-ra/Icon"
import Value from "aldoo-ra/Value"

const ArrowMenuMobile = ({
  menuGroupName = "PlanCategories",
  onSelectionChange,
  preProcessItem,
  itemRender,
  onData,
  styleOverrides = {},
  initialSelectedIndex = 0,
  defaultSelected = null,
  enableScrollNavigation = true,
  hiddenItems = [],
  refreshKey = 0,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    return Math.max(0, initialSelectedIndex) // Ensure it's at least 0
  })
  const [menuItemsLoaded, setMenuItemsLoaded] = useState(false)
  const [isTouchActive, setIsTouchActive] = useState(false)
  const [arrowPosition, setArrowPosition] = Value("arrowPosition", null)
  const scrollContainerRef = useRef(null)
  const menuItems = useRef([])
  const itemRefs = useRef([])
  const animationFrameRef = useRef(null)
  const isAnimatingRef = useRef(false)
  const touchStartX = useRef(0)
  const lastTouchX = useRef(0)
  const touchStartTime = useRef(0)
  const velocityRef = useRef(0)

  const SWIPE_THRESHOLD = 30
  const VELOCITY_THRESHOLD = 0.3
  const SWIPE_TIME_THRESHOLD = 350
  const SNAP_ANIMATION_DURATION = 250

  const getItemRef = useCallback((index) => {
    if (!itemRefs.current[index]) itemRefs.current[index] = React.createRef()
    return itemRefs.current[index]
  }, [])

  const getSelectedIndexFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const planParam = urlParams.get("plans")
    if (planParam && menuItems.current.length) {
      return (
        menuItems.current.findIndex(
          (item) => item.content?.options?.id === planParam
        ) || 0
      )
    }
    return initialSelectedIndex
  }, [initialSelectedIndex])

  const updateURL = useCallback((sectionId) => {
    if (!sectionId) return
    const params = new URLSearchParams(window.location.search)
    params.set("plans", sectionId)
    window.history.pushState({}, "", `/pricing?${params.toString()}`)
  }, [])

  const animateScroll = useCallback(
    (targetScroll, startScroll) => {
      if (!scrollContainerRef.current) return

      // Always cancel any existing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }

      isAnimatingRef.current = true
      const startTime = performance.now()

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / SNAP_ANIMATION_DURATION, 1)
        const easeProgress = 1 - Math.pow(1 - progress, 3) // Cubic ease-out

        if (scrollContainerRef.current) {
          const newScrollLeft =
            startScroll + (targetScroll - startScroll) * easeProgress
          scrollContainerRef.current.scrollLeft = newScrollLeft

          if (progress >= 1) {
            setArrowPosition(newScrollLeft)
            isAnimatingRef.current = false
            animationFrameRef.current = null
            return // End animation when complete
          }
        } else {
          // Scroll container no longer exists
          isAnimatingRef.current = false
          animationFrameRef.current = null
          return // End animation early
        }

        animationFrameRef.current = requestAnimationFrame(animate)
      }

      animationFrameRef.current = requestAnimationFrame(animate)

      // Safeguard: Ensure animation state is reset after max duration
      setTimeout(() => {
        if (isAnimatingRef.current) {
          isAnimatingRef.current = false
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
          }
        }
      }, SNAP_ANIMATION_DURATION + 100) // Add buffer to max duration
    },
    [setArrowPosition]
  )

  const centerSelectedItem = useCallback(() => {
    if (
      selectedIndex < 0 ||
      !scrollContainerRef.current ||
      isAnimatingRef.current
    ) {
      return
    }

    const selectedItem = itemRefs.current[selectedIndex]?.current
    if (!selectedItem) {
      return
    }

    // Store the index we're centering for verification later
    const centeringIndex = selectedIndex

    // The arrow is positioned at the center of the screen
    const arrowCenter = window.innerWidth / 2
    const itemRect = selectedItem.getBoundingClientRect()
    const itemCenter = itemRect.left + itemRect.width / 2

    // Calculate how much we need to scroll to align item center with arrow center
    const scrollDelta = itemCenter - arrowCenter
    let targetScroll = scrollContainerRef.current.scrollLeft + scrollDelta
    const startScroll = scrollContainerRef.current.scrollLeft

    // Clamp targetScroll to valid bounds
    const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
    targetScroll = Math.max(0, Math.min(targetScroll, maxScroll))

    animateScroll(targetScroll, startScroll)

    // Verify and correct centering after animation completes
    // Only if we're still on the same selected index
    setTimeout(() => {
      // Don't verify if selection changed or animation already running
      if (
        !scrollContainerRef.current ||
        !itemRefs.current[centeringIndex]?.current ||
        isAnimatingRef.current ||
        selectedIndex !== centeringIndex
      ) {
        return
      }

      const verifyItem = itemRefs.current[centeringIndex].current
      const verifyRect = verifyItem.getBoundingClientRect()
      const verifyCenter = verifyRect.left + verifyRect.width / 2
      const actualArrowCenter = window.innerWidth / 2
      const finalDistance = Math.abs(verifyCenter - actualArrowCenter)

      if (finalDistance > 5) {
        // Calculate exact correction needed
        const correctionDelta = verifyCenter - actualArrowCenter
        const currentScroll = scrollContainerRef.current.scrollLeft
        const correctedTarget = currentScroll + correctionDelta

        // Apply bounds checking to the correction
        const maxScroll = scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth
        const finalCorrectedTarget = Math.max(0, Math.min(correctedTarget, maxScroll))

        // Start correction animation
        animateScroll(finalCorrectedTarget, currentScroll)
      }
    }, 350) // After animation should complete (250ms + buffer)
  }, [selectedIndex, animateScroll])

  const findNearestItem = useCallback(() => {
    if (!scrollContainerRef.current || !itemRefs.current.length)
      return selectedIndex

    // The arrow is positioned at the center of the screen
    const arrowCenter = window.innerWidth / 2

    let nearestIndex = 0
    let minDistance = Infinity
    
    itemRefs.current.forEach((ref, index) => {
      const item = ref?.current
      if (!item) {
        return
      }

      const itemRect = item.getBoundingClientRect()
      const itemCenter = itemRect.left + itemRect.width / 2
      const distance = Math.abs(itemCenter - arrowCenter)

      if (distance < minDistance) {
        minDistance = distance
        nearestIndex = index
      }
    })

    return nearestIndex
  }, [selectedIndex])

  const handleSelection = useCallback(
    (index) => {
      if (
        index === selectedIndex ||
        index < 0 ||
        index >= menuItems.current.length ||
        isAnimatingRef.current
      ) {
        return
      }

      userInitiatedChangeRef.current = true
      setSelectedIndex(index)

      if (enableScrollNavigation) {
        const sectionId = menuItems.current[index]?.content?.options?.id
        if (sectionId) {
          const section = document.querySelector(
            `[data-category="${sectionId}"]`
          )
          section?.scrollIntoView({ behavior: "smooth", block: "start" })
          updateURL(sectionId)
        }
      }

      if (onSelectionChange) {
        onSelectionChange(index, menuItems.current[index])
      }
    },
    [selectedIndex, enableScrollNavigation, onSelectionChange, updateURL]
  )

  const handleTouchStart = useCallback((e) => {
    if (!scrollContainerRef.current) return
    setIsTouchActive(true)
    const touch = e.touches[0]
    touchStartX.current = touch.clientX
    lastTouchX.current = touch.clientX
    touchStartTime.current = Date.now()
    velocityRef.current = 0
    scrollContainerRef.current.dataset.startScroll =
      scrollContainerRef.current.scrollLeft
  }, [])

  const handleTouchMove = useCallback(
    (e) => {
      if (!isTouchActive || !scrollContainerRef.current) return

      const touch = e.touches[0]
      const currentX = touch.clientX
      const timeDelta = Date.now() - touchStartTime.current

      if (timeDelta > 0) {
        const instantVelocity = (currentX - lastTouchX.current) / timeDelta
        velocityRef.current = 0.8 * velocityRef.current + 0.2 * instantVelocity
      }
      lastTouchX.current = currentX

      const startScroll = parseFloat(
        scrollContainerRef.current.dataset.startScroll
      )
      const deltaX = touch.clientX - touchStartX.current
      const newScrollLeft = startScroll - deltaX
      scrollContainerRef.current.scrollLeft = newScrollLeft
      setArrowPosition(newScrollLeft) // Update during drag
    },
    [isTouchActive, setArrowPosition]
  )

  const handleTouchEnd = useCallback(() => {
    if (!scrollContainerRef.current) return
    setIsTouchActive(false)

    const touchDuration = Date.now() - touchStartTime.current
    const touchDistance = lastTouchX.current - touchStartX.current
    const absDistance = Math.abs(touchDistance)

    // Small movements with short duration = tap (let click handler work)
    if (absDistance < 10 && touchDuration < 200) {
      return
    }

    // Reset animation state if stuck
    if (isAnimatingRef.current && Date.now() - touchStartTime.current > 1000) {
      isAnimatingRef.current = false
    }

    const isSwipe =
      (absDistance > SWIPE_THRESHOLD && touchDuration < SWIPE_TIME_THRESHOLD) ||
      Math.abs(velocityRef.current) > VELOCITY_THRESHOLD

    if (isSwipe) {
      const direction = touchDistance > 0 ? -1 : 1
      const newIndex = Math.min(
        Math.max(selectedIndex + direction, 0),
        menuItems.current.length - 1
      )
      handleSelection(newIndex)
    } else {
      // Find nearest item and select it like a click
      const nearestIndex = findNearestItem()
      
      // Always use handleSelection for consistency with click behavior
      if (nearestIndex !== selectedIndex) {
        handleSelection(nearestIndex)
      } else {
        // Even if same index, ensure proper centering with correction
        centerSelectedItem()
      }
    }
  }, [
    selectedIndex,
    findNearestItem,
    handleSelection,
    centerSelectedItem,
  ])

  // Add cleanup effect to prevent memory leaks
  useEffect(() => {
    // Cleanup function to ensure references are reset
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isAnimatingRef.current = false
    }
  }, [])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // Remove any existing listeners first to prevent duplicates
    scrollContainer.removeEventListener("touchstart", handleTouchStart)
    scrollContainer.removeEventListener("touchmove", handleTouchMove)
    scrollContainer.removeEventListener("touchend", handleTouchEnd)

    // Add current listeners
    scrollContainer.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    })
    scrollContainer.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    })
    scrollContainer.addEventListener("touchend", handleTouchEnd, {
      passive: true,
    })

    // Set initial scroll position from stored value if available
    if (
      arrowPosition !== null &&
      scrollContainerRef.current &&
      !isAnimatingRef.current
    ) {
      scrollContainerRef.current.scrollLeft = arrowPosition
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("touchstart", handleTouchStart)
        scrollContainer.removeEventListener("touchmove", handleTouchMove)
        scrollContainer.removeEventListener("touchend", handleTouchEnd)
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isAnimatingRef.current = false
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, arrowPosition])

  // Handle external selection changes via initialSelectedIndex prop, but only if not user-initiated
  const userInitiatedChangeRef = useRef(false)

  useEffect(() => {
    const normalizedInitialIndex = Math.max(0, initialSelectedIndex)

    // Always update when menu items are loaded for the first time
    if (!userInitiatedChangeRef.current && menuItemsLoaded) {
      if (normalizedInitialIndex !== selectedIndex && !isAnimatingRef.current) {
        setSelectedIndex(normalizedInitialIndex)
      }
    }

    // Reset the user-initiated flag after a short delay
    if (userInitiatedChangeRef.current) {
      const timer = setTimeout(() => {
        userInitiatedChangeRef.current = false
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [initialSelectedIndex, selectedIndex, menuItemsLoaded])

  // Separate effect to handle external updates when initialSelectedIndex changes
  useEffect(() => {
    if (menuItemsLoaded && !userInitiatedChangeRef.current && !isAnimatingRef.current) {
      const normalizedInitialIndex = Math.max(0, initialSelectedIndex)
      if (normalizedInitialIndex !== selectedIndex) {
        setSelectedIndex(normalizedInitialIndex)
      }
    }
  }, [initialSelectedIndex])

  // Handle external selection changes via defaultSelected prop
  useEffect(() => {
    if (!defaultSelected || !menuItemsLoaded || userInitiatedChangeRef.current || isAnimatingRef.current) {
      return
    }

    const targetIndex = menuItems.current.findIndex(
      (item) => item.content?.options?.id === defaultSelected
    )

    if (targetIndex !== -1 && targetIndex !== selectedIndex) {
      setSelectedIndex(targetIndex)
    }
  }, [defaultSelected, menuItemsLoaded, selectedIndex])

  useEffect(() => {
    if (!isAnimatingRef.current) {
      centerSelectedItem()
    }
  }, [selectedIndex, centerSelectedItem])

  useEffect(() => {
    if (!enableScrollNavigation || !menuItemsLoaded) return

    const handleScroll = () => {
      if (isAnimatingRef.current) return

      const viewportMiddle = window.scrollY + window.innerHeight / 2
      let closestIndex = selectedIndex
      let minDistance = Infinity

      menuItems.current.forEach((item, index) => {
        const section = document.querySelector(
          `[data-category="${item.content?.options?.id}"]`
        )
        if (!section) return

        const rect = section.getBoundingClientRect()
        const sectionMiddle = window.scrollY + rect.top + rect.height / 2
        const distance = Math.abs(sectionMiddle - viewportMiddle)

        if (distance < minDistance) {
          minDistance = distance
          closestIndex = index
        }
      })

      if (closestIndex !== selectedIndex) {
        setSelectedIndex(closestIndex)
        updateURL(menuItems.current[closestIndex]?.content?.options?.id)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [selectedIndex, enableScrollNavigation, menuItemsLoaded, updateURL])

  return (
    <div className="fixed top-0 md:top-[0px] lg:top-[80px] left-0 w-full z-50 bg-white shadow-md">
      <div className="relative h-[80px] mx-auto">
        <div className="absolute -top-5 left-0 w-full flex justify-center pointer-events-none z-50">
          <Icon id="arrow_menu_mobile" className="w-auto h-[45px]" />
        </div>
        <div className="relative pt-[35px]">
          <div
            ref={scrollContainerRef}
            className="flex items-start overflow-x-auto w-full touch-pan-x [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            <div className="flex">
              <div style={{ width: `calc(50vw - 75px)`, flexShrink: 0 }} />
              <MenuGroup
                key={`menu-${menuGroupName}-${JSON.stringify(hiddenItems)}`}
                name={menuGroupName}
                skipRootItem
                renderAs="hlist"
                preProcessItem={preProcessItem}
                onData={(payload) => {
                  const items = payload?.data[0]?.content?.items || []
                  
                  // Filter out hidden items
                  const filteredItems = items.filter(item => 
                    !hiddenItems.includes(item.content?.options?.id)
                  )
                  
                  menuItems.current = filteredItems
                  
                  // Set initial selection if defaultSelected is provided and not filtered out
                  if (defaultSelected) {
                    const index = filteredItems.findIndex(
                      (item) => item.content?.options?.id === defaultSelected
                    )
                    
                    if (index !== -1) {
                      setSelectedIndex(index)
                    }
                  } else {
                    // Ensure selectedIndex matches initialSelectedIndex when menu loads
                    setSelectedIndex(initialSelectedIndex)
                  }
                  
                  setMenuItemsLoaded(true)
                  
                  // Schedule initial centering after DOM updates
                  setTimeout(() => {
                    if (!isAnimatingRef.current) {
                      centerSelectedItem()
                    }
                  }, 100)
                  
                  onData?.(payload)
                }}
                menuItemRender={(item, index) => {
                  const categoryId = item.options?.id
                  
                  // Check if this item should be hidden
                  if (hiddenItems.includes(categoryId)) {
                    return null
                  }

                  const isSelected = selectedIndex === index
                  const itemContent = itemRender ? (
                    itemRender(item, index, isSelected)
                  ) : (
                    <div
                      className={`min-w-[150px] h-[40px] flex items-center justify-center px-0 transition-all duration-300
                        ${
                          isSelected
                            ? "text-primary font-bold scale-105"
                            : "text-gray_2 scale-100"
                        }`}
                    >
                      <span className="text-base text-center">
                        {item.label}
                      </span>
                    </div>
                  )

                  return (
                    <div
                      key={index}
                      ref={getItemRef(index)}
                      onClick={() => handleSelection(index)}
                      className="snap-center menu-item relative"
                      style={{ transition: "transform 0.3s ease-in-out" }}
                    >
                      {itemContent}
                    </div>
                  )
                }}
              />
              <div style={{ width: `calc(50vw - 75px)`, flexShrink: 0 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ArrowMenuMobile